import { useEffect, useRef, useState } from "react";

/* La barra de menús de la planilla: Archivo, Editar, Ver… como en Sheets.
 *
 * ── POR QUÉ ────────────────────────────────────────────────────────────────
 *
 * Cada función nueva sumaba un botón o un desplegable a la barra de
 * herramientas, y ya no se encontraba nada. Los menús ordenan todo por tema —
 * donde la gente que viene de Excel o de Sheets lo va a buscar— y dejan en la
 * barra sólo lo de uso constante.
 *
 * No sabe nada de planillas: recibe `menus` —`[{ titulo, items }]`— y cada
 * item es `{ t, accion, atajo, marcado, desactivado }`, o `{ t, sub: [items] }`
 * para un submenú, o `null` para una línea.
 *
 * El `mousedown` no se lleva el foco: después de elegir una opción, el teclado
 * sigue en la celda.
 */

function Lista({ items, correr, nivel = 0 }) {
  const [sub, setSub] = useState(null);
  return (
    <div className={"esp-caida" + (nivel ? " esp-caida-sub" : "")} role="menu">
      {items.map((it, i) => {
        if (!it) return <hr key={i} />;
        const conSub = Array.isArray(it.sub);
        return (
          <div key={i} className="esp-caida-item" onMouseEnter={() => setSub(conSub ? i : null)}>
            <button
              type="button" role="menuitem" disabled={!!it.desactivado}
              aria-haspopup={conSub ? "true" : undefined} aria-expanded={conSub ? sub === i : undefined}
              onClick={() => (conSub ? setSub(sub === i ? null : i) : correr(it))}
            >
              <span className="esp-caida-tilde" aria-hidden={!it.marcado}>{it.marcado ? "✓ " : ""}</span>
              <span className="esp-caida-texto">{it.t}</span>
              {it.atajo && <kbd>{it.atajo}</kbd>}
              {conSub && <span aria-hidden="true">▸</span>}
            </button>
            {conSub && sub === i && <Lista items={it.sub} correr={correr} nivel={nivel + 1} />}
          </div>
        );
      })}
    </div>
  );
}

export default function BarraMenus({ menus }) {
  const [abierto, setAbierto] = useState(null);
  const raiz = useRef(null);

  useEffect(() => {
    if (abierto === null) return undefined;
    const afuera = (e) => { if (raiz.current && !raiz.current.contains(e.target)) setAbierto(null); };
    const escape = (e) => { if (e.key === "Escape") setAbierto(null); };
    window.addEventListener("mousedown", afuera);
    window.addEventListener("keydown", escape);
    return () => {
      window.removeEventListener("mousedown", afuera);
      window.removeEventListener("keydown", escape);
    };
  }, [abierto]);

  const correr = (it) => {
    if (it.desactivado || !it.accion) return;
    setAbierto(null);
    it.accion();
  };

  return (
    <div className="esp-menus" role="menubar" aria-label="Menús" ref={raiz} onMouseDown={(e) => e.preventDefault()}>
      {menus.map((m, i) => (
        <div key={m.titulo} className="esp-menus-raiz">
          <button
            type="button" role="menuitem" aria-haspopup="true" aria-expanded={abierto === i}
            className={"esp-menus-titulo" + (abierto === i ? " abierto" : "")}
            onClick={() => setAbierto(abierto === i ? null : i)}
            // Con un menú abierto, pasar por otro título lo abre (como en un escritorio).
            onMouseEnter={() => { if (abierto !== null && abierto !== i) setAbierto(i); }}
          >{m.titulo}</button>
          {abierto === i && <Lista items={m.items} correr={correr} />}
        </div>
      ))}
    </div>
  );
}
